/**
 * WebGL 渲染器
 * 基于 PixiJS 封装，提供高性能渲染能力
 */
import { Vector2, ThemeConfig, THEMES } from '../types';
import { PuzzlePiece } from '../game/PuzzlePiece';
import { SlicedPiece } from '../game/ImageSlicer';
import { TextureAtlasPacker, AtlasFrame } from './TextureAtlasPacker';
import { CoordinateSystem } from '../core/CoordinateSystem';

// PixiJS 类型声明（实际使用时需要引入 PixiJS）
declare const PIXI: any;

export interface RendererConfig {
    width: number;
    height: number;
    backgroundColor: number;
    antialias: boolean;
    resolution: number;
    interpolationFactor?: number;
    // 拼图逻辑尺寸（用于计算缩放因子）
    puzzleSize?: number;
    // 边距配置：支持数字（比例）或对象（像素或比例，<=1为比例，>1为像素）
    padding?: number | { top?: number; bottom?: number; left?: number; right?: number };
    // 投影效果配置
    dropShadow: {
        enabled: boolean;
        color: number;
        alpha: number;
        blur: number;
        offset: { x: number; y: number };
    };
}

interface PathSegment {
    type: 'M' | 'L' | 'C' | 'Z';
    points: number[]; // [x, y] for M/L/Z, [cp1x, cp1y, cp2x, cp2y, x, y] for C
    startPoint: Vector2; // Segment starting point
    length: number;
}

export interface RenderEvents {
    // 碎片交互事件
    onPieceClick?: (pieceId: string, position: Vector2) => void;
    onPieceDragStart?: (pieceId: string, position: Vector2) => void;
    onPieceDragMove?: (pieceId: string, position: Vector2) => void;
    onPieceDragEnd?: (pieceId: string, position: Vector2) => void;
    onPieceRightClick?: (pieceId: string, position: Vector2) => void;
    onPieceDoubleTap?: (pieceId: string, position: Vector2) => void;
    onPieceLongPress?: (pieceId: string, position: Vector2) => void;
    // 全局事件
    onWheel?: (position: Vector2, deltaY: number) => void;
    onKeyDown?: (key: string) => void;
    onKeyUp?: (key: string) => void;
}

export class WebGLRenderer {
    private app: any;  // PIXI.Application
    private container: HTMLElement;
    private pieceSprites: Map<string, any> = new Map();  // PIXI.Container
    private events: RenderEvents = {};
    private config: RendererConfig;
    private currentTheme: ThemeConfig = THEMES.ZEN_LIGHT; // Default theme

    // 层级容器
    private backgroundLayer: any;
    private baseMapLayer: any;  // 底图层（显示碎片正确位置的轮廓）
    private guideLayer: any;    // 引导图层（显示完整原图）
    private piecesLayer: any;
    private effectsLayer: any;
    private uiLayer: any;
    
    // 背景区域装饰（条栏和工作区）
    private barGraphics: any;      // PIXI.Graphics (Top/Bottom bars)
    private workAreaGraphics: any; // PIXI.Graphics (Inner game area)

    // 底图轮廓精灵
    private baseMapSprites: Map<string, any> = new Map();
    // 保存当前的 slicedPieces 引用，以便在切换主题时重绘
    private currentSlicedPieces: SlicedPiece[] = [];
    // 碎片数据映射 (ID -> SlicedPiece)，用于快速查找轮廓数据
    private pieceMap: Map<string, SlicedPiece> = new Map();

    // 图集相关
    private atlasTextures: any[] = [];  // PIXI.Texture[]
    private atlasFrames: Map<string, AtlasFrame> = new Map();
    private useAtlas: boolean = false;

    // 组容器相关
    private groupContainers: Map<string, any> = new Map();  // groupId -> PIXI.Container
    private pieceToGroup: Map<string, string> = new Map();  // pieceId -> groupId

    // 手势检测相关
    private longPressTimer: number | null = null;
    private longPressTime: number = 500;  // 长按触发时间（毫秒）
    private doubleTapTime: number = 300;  // 双击判定时间（毫秒）
    private lastTapTime: number = 0;
    private lastTapPieceId: string | null = null;

    // 调试：是否显示碎片编号
    private debugShowNumbers: boolean = false;

    // 拖拽状态跟踪（用于解决抖动问题）
    private draggingPieceId: string | null = null;
    private draggingGroupId: string | null = null;

    // 活跃的光效动画
    private activeGlows: Map<string, { 
        graphics: any, 
        segments: PathSegment[], 
        totalLength: number, 
        startTime: number, 
        duration: number,
        color: number
    }> = new Map();

    // 缩放相关
    private gameContainer: any = null;  // 游戏内容容器（包含底图层和碎片层）
    private scaleFactor: number = 1;    // 当前缩放因子
    private puzzleSize: number = 1024;  // 拼图逻辑尺寸

    constructor(container: HTMLElement, config?: Partial<RendererConfig>) {
        this.container = container;
        this.config = {
            width: container.clientWidth || 880,
            height: container.clientHeight || 880,
            backgroundColor: this.currentTheme.backgroundColor,
            antialias: true,
            resolution: window.devicePixelRatio || 1,
            interpolationFactor: 0.2, // 平滑移动系数 (0-1)
            puzzleSize: 1024,  // 默认拼图逻辑尺寸
            // 默认边距配置：上下固定像素，左右比例
            padding: { top: 100, bottom: 140, left: 0.15, right: 0.15 },
            dropShadow: {
                enabled: true,  // 启用投影
                color: 0x000000,
                alpha: 0.3,
                blur: 4,
                offset: { x: 4, y: 4 }
            },
            ...config
        };
    }

    /**
     * 设置主题
     */
    setTheme(theme: ThemeConfig): void {
        this.currentTheme = theme;
        
        // 更新背景颜色（外部 Table 颜色）
        if (this.app && this.app.renderer) {
            this.app.renderer.background.color = theme.backgroundColor;
        }
        
        // 更新工作区背景（内部 Board 颜色）
        this.drawWorkAreaBackground();

        // 解除底图缓存，准备重绘
        if (this.baseMapLayer) {
            this.baseMapLayer.cacheAsBitmap = false;
        }

        // 如果存在底图数据，重新绘制底图
        if (this.currentSlicedPieces.length > 0) {
            this.createBaseMap(this.currentSlicedPieces);
        }
    }

    /**
     * 初始化渲染器
     */
    async initialize(): Promise<void> {
        // 检查 PixiJS 是否加载
        if (typeof PIXI === 'undefined') {
            throw new Error('PixiJS is not loaded. Please include PixiJS script.');
        }

        // 重新获取容器的实际尺寸（此时 DOM 已经渲染完成）
        const actualWidth = this.container.clientWidth || this.config.width;
        const actualHeight = this.container.clientHeight || this.config.height;
        this.config.width = actualWidth;
        this.config.height = actualHeight;

        // 创建 PIXI 应用
        this.app = new PIXI.Application({
            width: this.config.width,
            height: this.config.height,
            backgroundColor: this.currentTheme.backgroundColor,
            antialias: this.config.antialias,
            resolution: this.config.resolution,
            autoDensity: true
        });

        // 添加到容器
        this.container.appendChild(this.app.view);

        // PixiJS v7 事件系统设置
        // 确保 stage 可以接收事件并传递给子元素
        this.app.stage.eventMode = 'static';
        this.app.stage.interactiveChildren = true;

        // 设置 stage 的 hitArea 覆盖整个画布，否则空白区域不会触发事件
        this.app.stage.hitArea = new PIXI.Rectangle(0, 0, this.config.width, this.config.height);

        // 创建背景层（不缩放）
        this.backgroundLayer = new PIXI.Container();
        this.app.stage.addChild(this.backgroundLayer);
        
        // 初始化背景条栏装饰 (添加到 backgroundLayer)
        this.barGraphics = new PIXI.Graphics();
        this.backgroundLayer.addChild(this.barGraphics);

        // 创建游戏内容容器（用于统一缩放底图层和碎片层）
        this.gameContainer = new PIXI.Container();
        this.app.stage.addChild(this.gameContainer);

        // 初始化工作区背景对象 (添加到 gameContainer 最底层)
        this.workAreaGraphics = new PIXI.Graphics();
        this.gameContainer.addChild(this.workAreaGraphics);

        // 在游戏容器内创建底图层和碎片层
        this.baseMapLayer = new PIXI.Container();  // 底图层在背景之上，碎片之下
        this.guideLayer = new PIXI.Container();    // 引导图层
        this.piecesLayer = new PIXI.Container();
        
        this.gameContainer.addChild(this.baseMapLayer);
        this.gameContainer.addChild(this.guideLayer);
        this.gameContainer.addChild(this.piecesLayer);

        // 创建效果层和UI层（不缩放，在游戏容器之上）
        this.effectsLayer = new PIXI.Container();
        this.uiLayer = new PIXI.Container();
        this.app.stage.addChild(this.effectsLayer);
        this.app.stage.addChild(this.uiLayer);

        // 启用交互排序
        this.piecesLayer.sortableChildren = true;

        // 设置 gameContainer 和 piecesLayer 的交互模式
        this.gameContainer.eventMode = 'static';
        this.gameContainer.interactiveChildren = true;
        this.piecesLayer.eventMode = 'static';
        this.piecesLayer.interactiveChildren = true;

        // 计算并应用缩放因子
        this.updateScale();
        
        // 绘制工作区背景
        this.drawWorkAreaBackground();

        // 设置视口缩放（移动端适配）
        this.setupViewport();

        // 设置全局事件
        this.setupGlobalEvents();
    }

    /**
     * 绘制背景区域装饰（上下条栏背景）
     */
    private drawWorkAreaBackground(): void {
        if (!this.barGraphics) return;

        // 清除拼图板背景（如果存在）
        if (this.workAreaGraphics) {
            this.workAreaGraphics.clear();
        }

        // 绘制上下条栏背景 (在 backgroundLayer 中，不随拼图缩放，铺满宽度)
        this.barGraphics.clear();
        
        // 解析 Padding 像素值
        const p = this.config.padding;
        const width = this.config.width;
        const height = this.config.height;
        let pt = 0, pb = 0;
        
        if (typeof p === 'number') {
            pt = pb = height * p;
        } else if (p) {
            pt = (p.top !== undefined && p.top <= 1) ? p.top * height : (p.top || 0);
            pb = (p.bottom !== undefined && p.bottom <= 1) ? p.bottom * height : (p.bottom || 0);
        }

        // 绘制顶栏背景
        this.barGraphics.beginFill(this.currentTheme.topBarColor);
        this.barGraphics.drawRect(0, 0, width, pt);
        this.barGraphics.endFill();

        // 绘制底栏背景
        this.barGraphics.beginFill(this.currentTheme.bottomBarColor);
        this.barGraphics.drawRect(0, height - pb, width, pb);
        this.barGraphics.endFill();
        
        // 给条栏加个细微的分割线
        this.barGraphics.lineStyle(1, 0x000000, 0.05);
        this.barGraphics.moveTo(0, pt);
        this.barGraphics.lineTo(width, pt);
        this.barGraphics.moveTo(0, height - pb);
        this.barGraphics.lineTo(width, height - pb);
    }

    /**
     * 设置全局事件（滚轮、键盘）
     */
    private setupGlobalEvents(): void {
        // 滚轮事件
        this.app.view.addEventListener('wheel', this.handleWheel, { passive: false });

        // 键盘事件
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('keyup', this.handleKeyUp);

        // 阻止右键菜单
        this.app.view.addEventListener('contextmenu', (e: Event) => e.preventDefault());
    }

    /**
     * 滚轮事件处理
     */
    private handleWheel = (e: WheelEvent): void => {
        e.preventDefault();
        const rect = this.app.view.getBoundingClientRect();
        const screenPos: Vector2 = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        // 转换为逻辑坐标
        const logicPos = this.screenToLogic(screenPos);
        this.events.onWheel?.(logicPos, e.deltaY);
    };

    /**
     * 键盘按下事件处理
     */
    private handleKeyDown = (e: KeyboardEvent): void => {
        this.events.onKeyDown?.(e.key);
    };

    /**
     * 键盘抬起事件处理
     */
    private handleKeyUp = (e: KeyboardEvent): void => {
        this.events.onKeyUp?.(e.key);
    };

    /**
     * 设置事件监听
     */
    setEvents(events: RenderEvents): void {
        this.events = { ...this.events, ...events };
    }

    /**
     * 设置视口（用于缩放和平移）
     */
    private setupViewport(): void {
        // 事件已在 initialize 中设置
        // 此方法保留用于将来扩展视口功能（缩放、平移等）
    }

    /**
     * 计算并更新缩放因子
     * 根据容器大小和拼图逻辑尺寸计算缩放因子，使拼图适配屏幕
     */
    private updateScale(): void {
        const width = this.config.width;
        const height = this.config.height;

        // 解析 Padding
        let pt = 0, pb = 0, pl = 0, pr = 0;
        const p = this.config.padding;
        
        if (typeof p === 'number') {
            pt = pb = height * p;
            pl = pr = width * p;
        } else if (p) {
            // 辅助函数：<= 1 视为比例，> 1 视为像素
            const resolve = (val: number | undefined, dim: number) => {
                if (val === undefined) return 0;
                return val <= 1 ? val * dim : val;
            };
            pt = resolve(p.top, height);
            pb = resolve(p.bottom, height);
            pl = resolve(p.left, width);
            pr = resolve(p.right, width);
        }

        // 计算可用区域
        const availableWidth = width - (pl + pr);
        const availableHeight = height - (pt + pb);

        // 计算缩放因子，取较小值以确保完全显示
        this.scaleFactor = Math.min(
            availableWidth / this.puzzleSize,
            availableHeight / this.puzzleSize
        );

        // 应用缩放到游戏容器
        if (this.gameContainer) {
            this.gameContainer.scale.set(this.scaleFactor);

            // 将游戏容器放置在计算出的中心位置
            const scaledSize = this.puzzleSize * this.scaleFactor;
            this.gameContainer.x = pl + (availableWidth - scaledSize) / 2;
            this.gameContainer.y = pt + (availableHeight - scaledSize) / 2;
        }

        console.log(`[WebGLRenderer] Scale updated: factor=${this.scaleFactor.toFixed(3)}, padding=T${pt}/B${pb}/L${pl}/R${pr}`);
    }

    /**
     * 设置拼图逻辑尺寸并更新缩放
     * @param size 拼图逻辑尺寸（通常是 imageSize，如 1024）
     */
    setPuzzleSize(size: number): void {
        this.puzzleSize = size;
        this.updateScale();
    }

    /**
     * 获取当前缩放因子
     */
    getScaleFactor(): number {
        return this.scaleFactor;
    }

    /**
     * 将屏幕坐标转换为逻辑坐标（用于事件处理）
     * @param screenPos 屏幕坐标（相对于画布）
     * @returns 逻辑坐标（1024坐标系）
     */
    screenToLogic(screenPos: Vector2): Vector2 {
        if (!this.gameContainer) {
            return screenPos;
        }
        return {
            x: (screenPos.x - this.gameContainer.x) / this.scaleFactor,
            y: (screenPos.y - this.gameContainer.y) / this.scaleFactor
        };
    }

    /**
     * 将逻辑坐标转换为屏幕坐标
     * @param logicPos 逻辑坐标（1024坐标系）
     * @returns 屏幕坐标（相对于画布）
     */
    logicToScreen(logicPos: Vector2): Vector2 {
        if (!this.gameContainer) {
            return logicPos;
        }
        return {
            x: logicPos.x * this.scaleFactor + this.gameContainer.x,
            y: logicPos.y * this.scaleFactor + this.gameContainer.y
        };
    }

    /**
     * 添加碎片精灵
     */
    addPieceSprite(piece: PuzzlePiece, slicedPiece: SlicedPiece, index?: number): void {
        // 创建碎片容器
        const container = new PIXI.Container();
        container.name = piece.id;

        // 从 Canvas 创建纹理
        const texture = PIXI.Texture.from(slicedPiece.texture);
        const sprite = new PIXI.Sprite(texture);

        // 不使用锚点，直接定位精灵的左上角
        sprite.anchor.set(0, 0);

        // 纹理左上角应该位于 bounds.x, bounds.y（相对于世界坐标）
        // 容器位置是 center，所以精灵的相对位置是 bounds.x - center.x
        const textureWidth = slicedPiece.texture.width;
        const textureHeight = slicedPiece.texture.height;
        sprite.x = slicedPiece.bounds.x - slicedPiece.center.x;
        sprite.y = slicedPiece.bounds.y - slicedPiece.center.y;

        // 调试日志
        if (index !== undefined && index < 3) {
            // 解析 pathData 起点
            const pathMatch = slicedPiece.pathData.match(/M\s*([-\d.]+)\s+([-\d.]+)/);
            const pathStartX = pathMatch ? parseFloat(pathMatch[1]) : NaN;
            const pathStartY = pathMatch ? parseFloat(pathMatch[2]) : NaN;

            // 计算精灵在世界坐标中的位置
            const worldSpriteX = piece.position.x + sprite.x;
            const worldSpriteY = piece.position.y + sprite.y;

            console.log(`[addPieceSprite] piece ${index} (${piece.coord.u},${piece.coord.v}):`);
            console.log(`  pathStart=(${pathStartX.toFixed(2)},${pathStartY.toFixed(2)})`);
            console.log(`  bounds.xy=(${slicedPiece.bounds.x.toFixed(2)},${slicedPiece.bounds.y.toFixed(2)}), size=${textureWidth}x${textureHeight}`);
            console.log(`  center=(${slicedPiece.center.x.toFixed(2)},${slicedPiece.center.y.toFixed(2)})`);
            console.log(`  container.pos=(${piece.position.x.toFixed(2)},${piece.position.y.toFixed(2)})`);
            console.log(`  sprite.pos=(${sprite.x.toFixed(2)},${sprite.y.toFixed(2)})`);
            console.log(`  worldSprite=(${worldSpriteX.toFixed(2)},${worldSpriteY.toFixed(2)})`);
            console.log(`  pathStart relative to bounds: (${(pathStartX - slicedPiece.bounds.x).toFixed(2)},${(pathStartY - slicedPiece.bounds.y).toFixed(2)})`);
        }

        container.addChild(sprite);

        // 设置初始位置
        container.x = piece.position.x;
        container.y = piece.position.y;
        container.rotation = piece.rotation * Math.PI / 180;

        // 设置交互
        container.eventMode = 'static';
        container.cursor = 'grab';
        // 设置 hitArea 为精灵的边界
        container.hitArea = new PIXI.Rectangle(
            sprite.x,
            sprite.y,
            textureWidth,
            textureHeight
        );

        this.setupPieceInteraction(container, piece.id);

        // 添加编号（仅调试模式）
        if (index !== undefined && this.debugShowNumbers) {
            this.addPieceNumber(container, index, slicedPiece.coord);
        }

        // 添加投影效果
        this.applyDropShadow(container);

        // 添加到层级
        this.piecesLayer.addChild(container);
        this.pieceSprites.set(piece.id, container);

        if (this.pieceSprites.size % 50 === 0 || this.pieceSprites.size === 1) {
            console.log(`Renderer: 已添加 ${this.pieceSprites.size} 个碎片精灵`);
        }
    }

    /**
     * 为碎片应用投影效果
     */
    private applyDropShadow(container: any, state: 'idle' | 'drag' | 'placed' = 'idle'): void {
        // 如果是 Placed 状态，移除所有阴影滤镜，模拟嵌入效果
        if (state === 'placed') {
            container.filters = [];
            return;
        }

        const shadowConfig = this.config.dropShadow;
        // 如果全局禁用，或者是 idle 且配置未启用，则不加阴影
        if (!shadowConfig.enabled) return;

        let distance = 4;
        let blur = 4;
        let alpha = shadowConfig.alpha;

        if (state === 'drag') {
            distance = 15; // 抬起高度
            blur = 10;     // 模糊度增加
            alpha = 0.6;   // 阴影更深
        } else {
            // Idle state
            distance = 4;
            blur = 2;
            alpha = 0.3;
        }

        // 使用 PixiJS 内置的 DropShadowFilter
        if (PIXI.filters && PIXI.filters.DropShadowFilter) {
            const dropShadowFilter = new PIXI.filters.DropShadowFilter({
                color: shadowConfig.color,
                alpha: alpha,
                blur: blur,
                distance: distance, // 使用 distance 代替 offset
                rotation: 45,       // 固定光源角度
                quality: 3
            });
            container.filters = [dropShadowFilter];
        } else {
            // Fallback
            this.createSimpleShadow(container);
        }
    }

    /**
     * 创建简单阴影（后备方案）
     */
    private createSimpleShadow(container: any): void {
        const shadow = this.config.dropShadow;
        if (!shadow.enabled) return;

        // 克隆主精灵作为阴影
        const mainSprite = container.children[0];
        if (!mainSprite) return;

        const shadowSprite = new PIXI.Sprite(mainSprite.texture);
        shadowSprite.anchor.set(0.5);
        shadowSprite.x = mainSprite.x + shadow.offset.x;
        shadowSprite.y = mainSprite.y + shadow.offset.y;
        shadowSprite.tint = shadow.color;
        shadowSprite.alpha = shadow.alpha;

        // 添加模糊滤镜
        if (PIXI.filters && PIXI.filters.BlurFilter) {
            shadowSprite.filters = [new PIXI.filters.BlurFilter(shadow.blur)];
        } else if (PIXI.BlurFilter) {
            shadowSprite.filters = [new PIXI.BlurFilter(shadow.blur)];
        }

        // 将阴影放在主精灵下方
        container.addChildAt(shadowSprite, 0);
    }

    /**
     * 为碎片添加编号（用于测试）
     */
    private addPieceNumber(container: any, index: number, coord: { u: number, v: number }): void {
        const labelText = `${index}\n(${coord.u},${coord.v})`;
        const text = new PIXI.Text(labelText, {
            fontFamily: 'Arial',
            fontSize: 12,
            fontWeight: 'bold',
            fill: 0xffffff,
            align: 'center',
            stroke: 0x000000,
            strokeThickness: 3
        });
        text.anchor.set(0.5);
        // 放在容器中心（Container 的 0,0 就是碎片中心）
        text.x = 0;
        text.y = 0;
        text.name = '__debugNumber__';
        container.addChild(text);
    }

    /**
     * 切换调试编号显示（控制台命令用）
     */
    toggleDebugNumbers(show?: boolean): void {
        this.debugShowNumbers = show !== undefined ? show : !this.debugShowNumbers;
        console.log(`[Debug] 碎片编号显示: ${this.debugShowNumbers ? 'ON' : 'OFF'}`);

        if (this.debugShowNumbers) {
            // 为所有已有碎片添加编号
            let index = 0;
            for (const [pieceId, container] of this.pieceSprites) {
                const slicedPiece = this.pieceMap.get(pieceId);
                if (slicedPiece) {
                    this.addPieceNumber(container, index, slicedPiece.coord);
                }
                index++;
            }
            // 重绘底图以显示编号
            if (this.currentSlicedPieces.length > 0) {
                this.createBaseMap(this.currentSlicedPieces);
            }
        } else {
            // 移除所有碎片上的编号文本
            for (const [, container] of this.pieceSprites) {
                const label = container.getChildByName?.('__debugNumber__');
                if (label) container.removeChild(label);
            }
            // 重绘底图以隐藏编号
            if (this.currentSlicedPieces.length > 0) {
                this.createBaseMap(this.currentSlicedPieces);
            }
        }
    }

    /**
     * 设置碎片交互
     */
    private setupPieceInteraction(container: any, pieceId: string): void {
        let isDragging = false;
        let dragOffset = { x: 0, y: 0 };
        let hasMoved = false;
        let pointerDownPos = { x: 0, y: 0 };

        const onPointerMove = (e: any) => {
            if (!isDragging) return;

            const screenPos = e.data.global;

            // 检测是否有明显移动（使用屏幕坐标检测）
            const dx = screenPos.x - pointerDownPos.x;
            const dy = screenPos.y - pointerDownPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved = true;
                // 移动时取消长按
                this.cancelLongPressTimer();
            }

            // 转换为逻辑坐标后传递给事件处理器
            const logicPos = this.screenToLogic(screenPos);
            this.events.onPieceDragMove?.(pieceId, logicPos);
        };

        const onPointerUp = (e: any) => {
            if (!isDragging) return;

            isDragging = false;
            container.cursor = 'grab';
            this.draggingPieceId = null;

            // 恢复普通阴影
            this.applyDropShadow(container, 'idle');

            // 移除全局监听
            this.app.stage.off('pointermove', onPointerMove);
            this.app.stage.off('pointerup', onPointerUp);
            this.app.stage.off('pointerupoutside', onPointerUp);

            // 取消长按计时
            this.cancelLongPressTimer();

            const screenPos = e.data.global;
            const logicPos = this.screenToLogic(screenPos);

            // 如果没有移动，检测双击
            if (!hasMoved) {
                this.checkDoubleTap(pieceId, logicPos);
            }

            this.events.onPieceDragEnd?.(pieceId, logicPos);
        };

        container.on('pointerdown', (e: any) => {
            console.log('pointerdown on piece:', pieceId, 'button:', e.data.button);
            const screenPos = e.data.global;
            const logicPos = this.screenToLogic(screenPos);

            // 右键旋转
            if (e.data.button === 2) {
                this.events.onPieceRightClick?.(pieceId, logicPos);
                return;
            }

            isDragging = true;
            hasMoved = false;
            container.cursor = 'grabbing';

            pointerDownPos = { x: screenPos.x, y: screenPos.y };
            dragOffset.x = container.x - logicPos.x;
            dragOffset.y = container.y - logicPos.y;

            // 提升到顶层
            this.bringToFront(pieceId);

            // 记录拖拽状态
            this.draggingPieceId = pieceId;

            // 应用拖拽阴影（悬浮效果）
            this.applyDropShadow(container, 'drag');

            // 添加全局监听，确保鼠标移动过快时也能响应
            this.app.stage.on('pointermove', onPointerMove);
            this.app.stage.on('pointerup', onPointerUp);
            this.app.stage.on('pointerupoutside', onPointerUp);

            // 开始长按计时
            this.startLongPressTimer(pieceId, logicPos);

            console.log('Calling onPieceDragStart, hasHandler:', !!this.events.onPieceDragStart);
            this.events.onPieceDragStart?.(pieceId, logicPos);
        });
    }

    /**
     * 开始长按计时
     */
    private startLongPressTimer(pieceId: string, position: Vector2): void {
        this.cancelLongPressTimer();

        this.longPressTimer = window.setTimeout(() => {
            this.events.onPieceLongPress?.(pieceId, position);
            this.longPressTimer = null;
        }, this.longPressTime);
    }

    /**
     * 取消长按计时
     */
    private cancelLongPressTimer(): void {
        if (this.longPressTimer !== null) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }

    /**
     * 检测双击
     */
    private checkDoubleTap(pieceId: string, position: Vector2): void {
        const now = Date.now();
        const timeDiff = now - this.lastTapTime;

        if (timeDiff < this.doubleTapTime && this.lastTapPieceId === pieceId) {
            // 触发双击事件
            this.events.onPieceDoubleTap?.(pieceId, position);
            // 重置状态，防止连续触发
            this.lastTapTime = 0;
            this.lastTapPieceId = null;
        } else {
            this.lastTapTime = now;
            this.lastTapPieceId = pieceId;
        }
    }

    /**
     * 插值函数
     */
    private lerp(start: number, end: number, t: number): number {
        return start + (end - start) * t;
    }

    /**
     * 角度插值函数（处理 360 度回绕）
     */
    private lerpAngle(a: number, b: number, t: number): number {
        const diff = this.shortestRotationDelta(a, b);
        return a + diff * t;
    }

    /**
     * 更新光效动画 (Draw -> Flash -> Fade)
     */
    private updateGlows(): void {
        const now = Date.now();
        const toRemove: string[] = [];

        for (const [pieceId, glow] of this.activeGlows.entries()) {
            if (!glow.graphics || !glow.segments) {
                toRemove.push(pieceId);
                continue;
            }

            const elapsed = now - glow.startTime;
            const progress = Math.min(elapsed / glow.duration, 1.0);

            if (progress >= 1.0) {
                toRemove.push(pieceId);
                continue;
            }

            const graphics = glow.graphics;
            graphics.clear();

            // 阶段 1: 绘制 (0% - 60%)
            if (progress < 0.6) {
                const drawProgress = progress / 0.6;
                const currentLength = glow.totalLength * drawProgress;
                
                graphics.lineStyle({
                    width: 3,
                    color: glow.color,
                    alpha: 1,
                    cap: PIXI.LINE_CAP.ROUND,
                    join: PIXI.LINE_JOIN.ROUND,
                    native: false
                });
                
                this.drawPartialPath(graphics, glow.segments, currentLength);
            }
            // 阶段 2: 闪烁 (60% - 80%)
            else if (progress < 0.8) {
                // 绘制完整路径
                const flashProgress = (progress - 0.6) / 0.2;
                // 宽度: 3 -> 6 -> 3, 透明度 1
                const width = 3 + Math.sin(flashProgress * Math.PI) * 3;
                
                graphics.lineStyle({
                    width: width,
                    color: 0xFFFFFF, // 闪烁白色高光
                    alpha: 1,
                    cap: PIXI.LINE_CAP.ROUND,
                    join: PIXI.LINE_JOIN.ROUND,
                    native: false
                });
                
                // 叠加原色光晕
                this.drawPartialPath(graphics, glow.segments, glow.totalLength);
                
                // 再画一层金色的细线在上面 (为了层次感，这里简化为只画一层变粗的)
                // 或者我们保持金色，只变粗变亮
                graphics.lineStyle({
                    width: width,
                    color: glow.color, // 保持金色，或者混合白色
                    alpha: 1,
                    native: false
                });
                this.drawPartialPath(graphics, glow.segments, glow.totalLength);
            }
            // 阶段 3: 淡出 (80% - 100%)
            else {
                const fadeProgress = (progress - 0.8) / 0.2;
                const alpha = 1 - fadeProgress;
                
                graphics.lineStyle({
                    width: 3,
                    color: glow.color,
                    alpha: alpha,
                    native: false
                });
                
                this.drawPartialPath(graphics, glow.segments, glow.totalLength);
            }
        }

        // 清理结束的特效
        for (const pieceId of toRemove) {
            const glow = this.activeGlows.get(pieceId);
            if (glow) {
                const container = this.pieceSprites.get(pieceId);
                if (container && glow.graphics) {
                    container.removeChild(glow.graphics);
                    glow.graphics.destroy();
                }
                this.activeGlows.delete(pieceId);
            }
        }
    }

    /**
     * 批量更新渲染状态（逻辑驱动渲染的核心方法）
     * @param pieces 所有碎片
     * @param groups 所有组
     */
    update(pieces: PuzzlePiece[], groups: any[]): void {
        this.updateGlows();

        const t = this.config.interpolationFactor || 0.2;
        const epsilon = 0.01;

        // 1. 更新碎片
        for (const piece of pieces) {
            const sprite = this.pieceSprites.get(piece.id);
            if (!sprite) continue;

            const groupId = piece.groupId;
            if (groupId) {
                // 如果在组内，更新相对于组中心的本地坐标
                // 组内碎片的本地坐标通常是静态的，除非组内重新排列
                // 这里主要确保旋转同步
                sprite.rotation = 0;
            } else {
                // 独立碎片，平滑更新世界坐标
                const targetX = piece.position.x;
                const targetY = piece.position.y;
                const targetRot = (piece.rotation * Math.PI) / 180;

                // 如果正在拖拽该碎片，直接设置位置，不使用插值
                if (this.draggingPieceId === piece.id) {
                    sprite.x = targetX;
                    sprite.y = targetY;
                    sprite.rotation = targetRot;
                } else {
                    if (Math.abs(sprite.x - targetX) > epsilon || Math.abs(sprite.y - targetY) > epsilon) {
                        sprite.x = this.lerp(sprite.x, targetX, t);
                        sprite.y = this.lerp(sprite.y, targetY, t);
                    } else {
                        sprite.x = targetX;
                        sprite.y = targetY;
                    }

                    const rotDiff = this.shortestRotationDelta(sprite.rotation, targetRot);
                    if (Math.abs(rotDiff) > epsilon) {
                        // 使用 shortestRotationDelta 计算出的差值进行累加，确保方向正确
                        sprite.rotation += rotDiff * t;
                    } else {
                        sprite.rotation = targetRot;
                    }
                }
            }

            piece.clearDirty();
        }

        // 2. 更新组
        for (const group of groups) {
            const container = this.groupContainers.get(group.id);
            if (!container) continue;

            const targetX = group.center.x;
            const targetY = group.center.y;
            const targetRot = (group.rotation * Math.PI) / 180;

            // 如果正在拖拽该组，直接设置位置，不使用插值
            if (this.draggingGroupId === group.id) {
                container.x = targetX;
                container.y = targetY;
                container.rotation = targetRot;
            } else {
                if (Math.abs(container.x - targetX) > epsilon || Math.abs(container.y - targetY) > epsilon) {
                    container.x = this.lerp(container.x, targetX, t);
                    container.y = this.lerp(container.y, targetY, t);
                } else {
                    container.x = targetX;
                    container.y = targetY;
                }

                const rotDiff = this.shortestRotationDelta(container.rotation, targetRot);
                if (Math.abs(rotDiff) > epsilon) {
                    container.rotation += rotDiff * t;
                } else {
                    container.rotation = targetRot;
                }
            }

            group.clearDirty();
        }
    }

    /**
     * 同步碎片的逻辑状态到渲染层
     * 目前主要处理归位状态的阴影显示
     */
    syncPieceState(piece: PuzzlePiece): void {
        const pieceSprite = this.pieceSprites.get(piece.id);
        if (!pieceSprite) return;

        // 如果在口袋中，直接隐藏
        if (piece.isInPocket) {
            pieceSprite.visible = false;
            return;
        }

        pieceSprite.visible = true;

        if (piece.isPlaced) {
            this.applyDropShadow(pieceSprite, 'placed');
            this.hideBaseMapPiece(piece.id);
        } else {
            this.applyDropShadow(pieceSprite, 'idle');
            this.showBaseMapPiece(piece.id);
        }
    }

    /**
     * 批量同步所有碎片状态
     */
    syncAllPieceStates(pieces: PuzzlePiece[]): void {
        for (const piece of pieces) {
            this.syncPieceState(piece);
        }
    }

    /**
     * 更新碎片显示
     */
    updatePieceSprite(piece: PuzzlePiece): void {
        const sprite = this.pieceSprites.get(piece.id);
        if (sprite) {
            // 如果碎片已经在组容器中，其坐标系已变为本地坐标系，
            // 不应直接使用 piece.position (世界坐标) 来更新。
            if (this.pieceToGroup.has(piece.id)) return;

            sprite.x = piece.position.x;
            sprite.y = piece.position.y;
            sprite.rotation = (piece.rotation * Math.PI) / 180;
        }
    }

    /**
     * 动画过渡
     */
    private animateTo(
        target: any,
        props: { x?: number; y?: number; rotation?: number },
        duration: number
    ): void {
        const startProps = {
            x: target.x,
            y: target.y,
            rotation: target.rotation
        };

        // 计算旋转的最短路径
        let rotationDelta = 0;
        if (props.rotation !== undefined) {
            rotationDelta = this.shortestRotationDelta(startProps.rotation, props.rotation);
        }

        const startTime = performance.now();

        // 记录动画开始时 sprite 的父容器，用于检测 reparent
        const originalParent = target.parent;
        // 记录动画版本号，用于检测外部强制设置位置
        const animVersion = (target.__animVersion || 0);
        target.__animVersion = animVersion;

        const animate = () => {
            // 如果 sprite 被移到了另一个容器（组合并），或者外部强制修改了位置版本，立即停止动画
            if (target.parent !== originalParent || target.__animVersion !== animVersion) {
                return;
            }

            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // 缓动函数
            const eased = this.easeOutCubic(progress);

            if (props.x !== undefined) {
                target.x = startProps.x + (props.x - startProps.x) * eased;
            }
            if (props.y !== undefined) {
                target.y = startProps.y + (props.y - startProps.y) * eased;
            }
            if (props.rotation !== undefined) {
                // 使用最短路径进行旋转动画
                target.rotation = startProps.rotation + rotationDelta * eased;
            }

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        animate();
    }

    /**
     * 计算两个角度之间的最短旋转增量（弧度）
     */
    private shortestRotationDelta(from: number, to: number): number {
        const PI2 = Math.PI * 2;
        // 标准化到 [0, 2π) 范围
        const normalizedFrom = ((from % PI2) + PI2) % PI2;
        const normalizedTo = ((to % PI2) + PI2) % PI2;

        let delta = normalizedTo - normalizedFrom;

        // 选择最短路径
        if (delta > Math.PI) {
            delta -= PI2;
        } else if (delta < -Math.PI) {
            delta += PI2;
        }

        return delta;
    }

    /**
     * 缓动函数
     */
    private easeOutCubic(t: number): number {
        return 1 - Math.pow(1 - t, 3);
    }

    /**
     * 手动启动碎片的拖拽（用于从外部 UI 拖入场景）
     */
    startManualDrag(pieceId: string): void {
        const container = this.pieceSprites.get(pieceId);
        if (!container) return;

        // 1. 确定是单片还是组，并提升层级
        const groupId = this.getPieceGroupId(pieceId);
        const targetContainer = groupId ? this.groupContainers.get(groupId) : container;
        
        if (groupId) {
            this.bringGroupToFront(groupId);
            this.draggingGroupId = groupId;
        } else {
            this.bringToFront(pieceId);
            this.draggingPieceId = pieceId;
        }

        // 2. 应用拖拽阴影
        this.applyDropShadow(targetContainer, 'drag');

        // 3. 核心：在 PIXI Stage 上建立临时全局监听，接管鼠标移动
        const onPointerMove = (e: any) => {
            const logicPos = this.screenToLogic(e.data.global);
            this.events.onPieceDragMove?.(pieceId, logicPos);
        };

        const onPointerUp = (e: any) => {
            // 清理监听
            this.app.stage.off('pointermove', onPointerMove);
            this.app.stage.off('pointerup', onPointerUp);
            this.app.stage.off('pointerupoutside', onPointerUp);

            this.draggingPieceId = null;
            this.draggingGroupId = null;
            this.applyDropShadow(targetContainer, 'idle');

            const logicPos = this.screenToLogic(e.data.global);
            this.events.onPieceDragEnd?.(pieceId, logicPos);
        };

        this.app.stage.on('pointermove', onPointerMove);
        this.app.stage.on('pointerup', onPointerUp);
        this.app.stage.on('pointerupoutside', onPointerUp);
    }

    /**
     * 将碎片提升到顶层
     */
    bringToFront(pieceId: string): void {
        const container = this.pieceSprites.get(pieceId);
        if (!container) return;

        // 如果在组内，提升整个组
        const groupId = this.pieceToGroup.get(pieceId);
        if (groupId) {
            this.bringGroupToFront(groupId);
            return;
        }

        if (container.parent) {
            container.parent.addChild(container);
        }
    }

    /**
     * 高亮碎片边缘（吸附成功效果） - 使用矢量绘制，高性能
     */
    highlightPiece(pieceId: string, color: number = 0xFFD700, duration: number = 600): void {
        const container = this.pieceSprites.get(pieceId);
        const slicedPiece = this.pieceMap.get(pieceId);
        
        if (!container || !slicedPiece) return;

        // 如果该碎片已有活跃特效，先清理旧的
        if (this.activeGlows.has(pieceId)) {
            const oldGlow = this.activeGlows.get(pieceId)!;
            if (oldGlow.graphics) {
                container.removeChild(oldGlow.graphics);
                oldGlow.graphics.destroy();
            } else if ((oldGlow as any).filter) {
                if (container.filters) {
                    container.filters = container.filters.filter((f: any) => f !== (oldGlow as any).filter);
                }
            }
            this.activeGlows.delete(pieceId);
        }

        // 解析路径用于动画
        const { segments, totalLength } = this.parsePathToSegments(slicedPiece.pathData);

        // 创建 Graphics 对象
        const graphics = new PIXI.Graphics();
        graphics.blendMode = PIXI.BLEND_MODES.ADD;
        // 设置轴心点
        graphics.pivot.set(slicedPiece.center.x, slicedPiece.center.y);
        
        container.addChild(graphics);

        // 注册动画
        this.activeGlows.set(pieceId, {
            graphics: graphics,
            segments: segments,
            totalLength: totalLength,
            startTime: Date.now(),
            duration: duration,
            color: color
        });
    }

    /**
     * 获取碎片精灵
     */
    getPieceSprite(pieceId: string): any {
        return this.pieceSprites.get(pieceId) || null;
    }

    /**
     * 移除碎片精灵
     */
    removePieceSprite(pieceId: string): void {
        const container = this.pieceSprites.get(pieceId);
        if (container) {
            this.piecesLayer.removeChild(container);
            container.destroy({ children: true, texture: true });
            this.pieceSprites.delete(pieceId);
        }
    }

    /**
     * 清空所有碎片
     */
    clearPieces(): void {
        for (const [id] of this.pieceSprites) {
            this.removePieceSprite(id);
        }
        // 清空组容器
        this.clearGroups();
    }

    // ==================== 组容器相关方法 ====================

    /**
     * 创建组容器
     * 将两个碎片的 sprite 合并到一个新的组容器中
     *
     * @param groupId 组ID
     * @param piece1Id 碎片1的ID
     * @param piece2Id 碎片2的ID
     * @param groupCenter 组中心（数据模型中的位置）
     * @param groupRotation 组旋转角度（度）
     * @param piece1Pos 碎片1在数据模型中的位置（用于正确定位）
     * @param piece2Pos 碎片2在数据模型中的位置（用于正确定位）
     */
    createGroupContainer(
        groupId: string,
        piece1Id: string,
        piece2Id: string,
        groupCenter: Vector2,
        groupRotation: number,
        piece1Pos?: Vector2,
        piece2Pos?: Vector2,
        order: 'piece1Above' | 'piece2Above' = 'piece1Above'
    ): void {
        const sprite1 = this.pieceSprites.get(piece1Id);
        const sprite2 = this.pieceSprites.get(piece2Id);

        if (!sprite1 || !sprite2) {
            console.warn(`[WebGLRenderer] Cannot create group: missing sprites`);
            return;
        }

        // 记录 sprite 当前世界坐标（在移动到组容器之前）
        const sprite1WorldPos = { x: sprite1.x, y: sprite1.y };
        const sprite2WorldPos = { x: sprite2.x, y: sprite2.y };

        console.log(`[WebGLRenderer] createGroupContainer:`, JSON.stringify({
            groupCenter,
            sprite1WorldPos,
            sprite2WorldPos,
            piece1Pos,
            piece2Pos,
            order
        }));

        // 创建组容器
        const groupContainer = new PIXI.Container();
        groupContainer.sortableChildren = false; // 我们手动管理顺序

        // 设置组容器位置为组中心
        groupContainer.x = groupCenter.x;
        groupContainer.y = groupCenter.y;

        // 设置组的旋转角度（弧度）
        groupContainer.rotation = (groupRotation * Math.PI) / 180;

        // 计算每个碎片相对于组中心的本地坐标（需要反向旋转）
        const angleRad = -groupContainer.rotation;
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);

        // 使用数据模型中的位置作为目标位置
        const targetPos1 = piece1Pos || sprite1WorldPos;
        const targetPos2 = piece2Pos || sprite2WorldPos;

        // 计算 sprite1 的目标本地坐标（基于数据模型位置）
        const targetOffset1 = {
            x: targetPos1.x - groupCenter.x,
            y: targetPos1.y - groupCenter.y
        };
        const targetLocalX1 = targetOffset1.x * cos - targetOffset1.y * sin;
        const targetLocalY1 = targetOffset1.x * sin + targetOffset1.y * cos;

        // 计算 sprite1 的当前本地坐标（基于 sprite 实际世界位置，保持视觉连续性）
        const currentOffset1 = {
            x: sprite1WorldPos.x - groupCenter.x,
            y: sprite1WorldPos.y - groupCenter.y
        };
        const currentLocalX1 = currentOffset1.x * cos - currentOffset1.y * sin;
        const currentLocalY1 = currentOffset1.x * sin + currentOffset1.y * cos;

        // 计算 sprite2 的目标本地坐标
        const targetOffset2 = {
            x: targetPos2.x - groupCenter.x,
            y: targetPos2.y - groupCenter.y
        };
        const targetLocalX2 = targetOffset2.x * cos - targetOffset2.y * sin;
        const targetLocalY2 = targetOffset2.x * sin + targetOffset2.y * cos;

        // 计算 sprite2 的当前本地坐标
        const currentOffset2 = {
            x: sprite2WorldPos.x - groupCenter.x,
            y: sprite2WorldPos.y - groupCenter.y
        };
        const currentLocalX2 = currentOffset2.x * cos - currentOffset2.y * sin;
        const currentLocalY2 = currentOffset2.x * sin + currentOffset2.y * cos;

        // 移动 sprite 到组容器
        if (sprite1.parent) sprite1.parent.removeChild(sprite1);
        if (sprite2.parent) sprite2.parent.removeChild(sprite2);

        sprite1.rotation = 0;
        sprite2.rotation = 0;

        // 根据 order 决定添加顺序（后添加的在上面）
        if (order === 'piece1Above') {
            // sprite2 在下，sprite1 在上
            sprite2.x = currentLocalX2;
            sprite2.y = currentLocalY2;
            groupContainer.addChild(sprite2);

            sprite1.x = currentLocalX1;
            sprite1.y = currentLocalY1;
            groupContainer.addChild(sprite1);
        } else {
            // sprite1 在下，sprite2 在上
            sprite1.x = currentLocalX1;
            sprite1.y = currentLocalY1;
            groupContainer.addChild(sprite1);

            sprite2.x = currentLocalX2;
            sprite2.y = currentLocalY2;
            groupContainer.addChild(sprite2);
        }

        // 动画移动到目标位置
        if (Math.abs(targetLocalX1 - currentLocalX1) > 0.1 || Math.abs(targetLocalY1 - currentLocalY1) > 0.1) {
            this.animateTo(sprite1, { x: targetLocalX1, y: targetLocalY1 }, 150);
        }
        if (Math.abs(targetLocalX2 - currentLocalX2) > 0.1 || Math.abs(targetLocalY2 - currentLocalY2) > 0.1) {
            this.animateTo(sprite2, { x: targetLocalX2, y: targetLocalY2 }, 150);
        }

        // 添加到 piecesLayer
        this.piecesLayer.addChild(groupContainer);

        // 设置交互
        this.setupGroupInteraction(groupContainer, groupId);

        // 记录映射
        this.groupContainers.set(groupId, groupContainer);
        this.pieceToGroup.set(piece1Id, groupId);
        this.pieceToGroup.set(piece2Id, groupId);

        console.log(`[WebGLRenderer] Created group container ${groupId} with pieces: ${piece1Id}, ${piece2Id} (order: ${order})`);
    }

    /**
     * 将碎片添加到现有组容器
     */
    addPieceToGroupContainer(
        groupId: string,
        pieceId: string,
        groupCenter: Vector2,
        groupRotation: number,
        targetPieceId?: string,
        order: 'above' | 'below' = 'above',
        piecePos?: Vector2
    ): void {
        const groupContainer = this.groupContainers.get(groupId);
        const sprite = this.pieceSprites.get(pieceId);

        if (!groupContainer || !sprite) {
            console.warn(`[WebGLRenderer] Cannot add piece to group: missing container or sprite`);
            return;
        }

        // 1. 处理组中心偏移
        // 当新碎片加入时，组中心会重新计算。如果中心发生了变化，
        // 我们需要调整现有子元素的本地坐标，以保持它们的世界坐标不变。
        const dx = groupCenter.x - groupContainer.x;
        const dy = groupCenter.y - groupContainer.y;

        if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
            // 将世界坐标系的位移转换为本地坐标系的位移
            // 注意：这里的位移转换不带旋转，因为容器本身的旋转还没变
            const localDelta = CoordinateSystem.worldToLocal(
                { x: groupContainer.x + dx, y: groupContainer.y + dy },
                { x: groupContainer.x, y: groupContainer.y },
                groupRotation
            );

            for (const child of groupContainer.children) {
                child.x -= localDelta.x;
                child.y -= localDelta.y;
                // 递增动画版本号，取消该 sprite 上残留的 animateTo 动画
                child.__animVersion = (child.__animVersion || 0) + 1;
            }

            // 更新容器位置
            groupContainer.x = groupCenter.x;
            groupContainer.y = groupCenter.y;
        }

        // 2. 更新旋转
        groupContainer.rotation = (groupRotation * Math.PI) / 180;

        // 3. 计算新碎片的本地坐标（相对于新中心）
        const targetPos = piecePos || { x: sprite.x, y: sprite.y };
        const localPos = CoordinateSystem.worldToLocal(targetPos, groupCenter, groupRotation);

        // 移动 sprite 到组容器
        if (sprite.parent) {
            sprite.parent.removeChild(sprite);
        }
        sprite.x = localPos.x;
        sprite.y = localPos.y;
        sprite.rotation = 0;

        // 插入到正确的位置
        if (targetPieceId && groupContainer.children.length > 0) {
            const targetSprite = this.pieceSprites.get(targetPieceId);
            if (targetSprite && targetSprite.parent === groupContainer) {
                const targetIndex = groupContainer.getChildIndex(targetSprite);
                const insertIndex = order === 'above' ? targetIndex + 1 : targetIndex;
                // 确保索引在有效范围内
                const finalIndex = Math.min(Math.max(insertIndex, 0), groupContainer.children.length);
                groupContainer.addChildAt(sprite, finalIndex);
            } else {
                groupContainer.addChild(sprite);
            }
        } else {
            groupContainer.addChild(sprite);
        }

        // 更新映射
        this.pieceToGroup.set(pieceId, groupId);

        // 核心修正：确保加入组的碎片一定失去了独立交互能力，防止拖拽死锁
        this.disablePieceInteraction(pieceId);

        console.log(`[WebGLRenderer] Added piece ${pieceId} to group ${groupId}`);
    }

    /**
     * 合并两个组容器
     */
    mergeGroupContainers(
        survivingGroupId: string,
        mergedGroupId: string,
        newCenter: Vector2,
        newRotation: number,
        piecePositions?: Record<string, Vector2>
    ): void {
        const survivingContainer = this.groupContainers.get(survivingGroupId);
        const mergedContainer = this.groupContainers.get(mergedGroupId);

        if (!survivingContainer || !mergedContainer) {
            console.warn(`[WebGLRenderer] Cannot merge groups: missing containers`);
            return;
        }

        // 1. 处理存活组中心偏移
        const dx = newCenter.x - survivingContainer.x;
        const dy = newCenter.y - survivingContainer.y;

        if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
            const angleRad = -survivingContainer.rotation;
            const cos = Math.cos(angleRad);
            const sin = Math.sin(angleRad);
            const localDX = dx * cos - dy * sin;
            const localDY = dx * sin + dy * cos;

            for (const child of survivingContainer.children) {
                child.x -= localDX;
                child.y -= localDY;
                // 递增动画版本号，取消该 sprite 上残留的 animateTo 动画
                child.__animVersion = (child.__animVersion || 0) + 1;
            }

            survivingContainer.x = newCenter.x;
            survivingContainer.y = newCenter.y;
        }

        // 2. 更新旋转
        survivingContainer.rotation = (newRotation * Math.PI) / 180;

        const angleRad = -survivingContainer.rotation;
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);

        // 将被合并组的所有子元素移到存活组
        const childrenToMove = [...mergedContainer.children];
        for (const child of childrenToMove) {
            // 获取该子元素对应的碎片 ID
            let pieceId: string | null = null;
            for (const [id, s] of this.pieceSprites.entries()) {
                if (s === child) {
                    pieceId = id;
                    break;
                }
            }

            // 优先使用传入的 piecePositions 中的逻辑位置
            let worldPos: Vector2;
            if (pieceId && piecePositions && piecePositions[pieceId]) {
                worldPos = piecePositions[pieceId];
            } else {
                // 回退到计算当前世界坐标
                worldPos = CoordinateSystem.localToWorld(
                    { x: child.x, y: child.y },
                    { x: mergedContainer.x, y: mergedContainer.y },
                    (mergedContainer.rotation * 180) / Math.PI
                );
            }

            // 计算相对于新组中心的本地坐标
            const localPos = CoordinateSystem.worldToLocal(worldPos, newCenter, newRotation);

            mergedContainer.removeChild(child);
            child.x = localPos.x;
            child.y = localPos.y;
            // 递增动画版本号，取消该 sprite 上残留的 animateTo 动画
            child.__animVersion = (child.__animVersion || 0) + 1;
            survivingContainer.addChild(child);

            // 关键修正：更新渲染器内部的碎片到组的映射
            if (pieceId) {
                this.pieceToGroup.set(pieceId, survivingGroupId);
            }
        }

        // 删除被合并的组容器
        this.piecesLayer.removeChild(mergedContainer);
        mergedContainer.destroy();
        this.groupContainers.delete(mergedGroupId);

        console.log(`[WebGLRenderer] Merged group ${mergedGroupId} into ${survivingGroupId}`);
    }

    /**
     * 更新组容器的位置和旋转
     */
    updateGroupContainer(groupId: string, center: Vector2, rotation: number): void {
        const groupContainer = this.groupContainers.get(groupId);
        if (!groupContainer) return;

        groupContainer.x = center.x;
        groupContainer.y = center.y;
        groupContainer.rotation = (rotation * Math.PI) / 180;
    }

    /**
     * 设置组容器的交互
     */
    private setupGroupInteraction(groupContainer: any, groupId: string): void {
        groupContainer.eventMode = 'static';
        groupContainer.cursor = 'grab';

        let isDragging = false;
        let dragOffset = { x: 0, y: 0 };
        let hasMoved = false;
        let pointerDownPos = { x: 0, y: 0 };

        // 获取组内第一个碎片的ID（用于事件回调）
        const getFirstPieceId = (): string => {
            for (const [pieceId, gId] of this.pieceToGroup.entries()) {
                if (gId === groupId) return pieceId;
            }
            return groupId;
        };

        const onPointerMove = (e: any) => {
            if (!isDragging) return;

            const screenPos = e.data.global;

            // 检测是否有明显移动（使用屏幕坐标检测）
            const dx = screenPos.x - pointerDownPos.x;
            const dy = screenPos.y - pointerDownPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved = true;
                this.cancelLongPressTimer();
            }

            // 转换为逻辑坐标后传递给事件处理器
            const logicPos = this.screenToLogic(screenPos);
            const pieceId = getFirstPieceId();
            this.events.onPieceDragMove?.(pieceId, logicPos);
        };

        const onPointerUp = (e: any) => {
            if (!isDragging) return;

            isDragging = false;
            groupContainer.cursor = 'grab';
            this.draggingGroupId = null;

            // 移除全局监听
            this.app.stage.off('pointermove', onPointerMove);
            this.app.stage.off('pointerup', onPointerUp);
            this.app.stage.off('pointerupoutside', onPointerUp);

            this.cancelLongPressTimer();

            const screenPos = e.data.global;
            const logicPos = this.screenToLogic(screenPos);
            const pieceId = getFirstPieceId();

            if (!hasMoved) {
                this.checkDoubleTap(pieceId, logicPos);
            }

            this.events.onPieceDragEnd?.(pieceId, logicPos);
        };

        groupContainer.on('pointerdown', (e: any) => {
            const pieceId = getFirstPieceId();
            console.log('pointerdown on group:', groupId, 'pieceId:', pieceId);

            const screenPos = e.data.global;
            const logicPos = this.screenToLogic(screenPos);

            // 右键旋转
            if (e.data.button === 2) {
                this.events.onPieceRightClick?.(pieceId, logicPos);
                return;
            }

            isDragging = true;
            hasMoved = false;
            groupContainer.cursor = 'grabbing';

            pointerDownPos = { x: screenPos.x, y: screenPos.y };
            dragOffset.x = groupContainer.x - logicPos.x;
            dragOffset.y = groupContainer.y - logicPos.y;

            // 提升到顶层
            this.bringGroupToFront(groupId);

            // 记录拖拽状态
            this.draggingGroupId = groupId;

            // 添加全局监听
            this.app.stage.on('pointermove', onPointerMove);
            this.app.stage.on('pointerup', onPointerUp);
            this.app.stage.on('pointerupoutside', onPointerUp);

            // 开始长按计时
            this.startLongPressTimer(pieceId, logicPos);

            this.events.onPieceDragStart?.(pieceId, logicPos);
        });
    }

    /**
     * 将组容器提升到顶层
     */
    bringGroupToFront(groupId: string): void {
        const groupContainer = this.groupContainers.get(groupId);
        if (groupContainer && groupContainer.parent) {
            groupContainer.parent.addChild(groupContainer);
        }
    }

    /**
     * 获取碎片所属的组ID
     */
    getPieceGroupId(pieceId: string): string | null {
        return this.pieceToGroup.get(pieceId) || null;
    }

    /**
     * 获取组容器
     */
    getGroupContainer(groupId: string): any {
        return this.groupContainers.get(groupId);
    }

    /**
     * 解散所有组容器（将碎片移回 piecesLayer）
     * 用于重新洗牌或重置游戏时
     */
    dissolveGroups(): void {
        for (const [groupId, groupContainer] of this.groupContainers) {
            // 将所有子元素（碎片精灵）移回 piecesLayer
            const children = [...groupContainer.children];
            for (const child of children) {
                // 计算世界坐标
                const worldPos = groupContainer.toGlobal(child.position);
                // 转换回 piecesLayer 的本地坐标（通常 piecesLayer 在 0,0，所以就是世界坐标）
                // 但为了保险，使用 toLocal
                const localPos = this.piecesLayer.toLocal(worldPos);

                child.x = localPos.x;
                child.y = localPos.y;
                child.rotation = child.rotation + groupContainer.rotation; // 累加旋转

                groupContainer.removeChild(child);
                this.piecesLayer.addChild(child);
            }

            // 销毁组容器
            this.piecesLayer.removeChild(groupContainer);
            groupContainer.destroy();
        }

        this.groupContainers.clear();
        this.pieceToGroup.clear();
        console.log('[WebGLRenderer] All groups dissolved');
    }

    /**
     * 清空所有组容器
     */
    clearGroups(): void {
        for (const [, container] of this.groupContainers) {
            this.piecesLayer.removeChild(container);
            container.destroy({ children: true }); // 明确销毁子元素
        }
        this.groupContainers.clear();
        this.pieceToGroup.clear();
    }

    /**
     * 禁用组内碎片的单独交互（已在组容器上处理）
     */
    disablePieceInteraction(pieceId: string): void {
        const sprite = this.pieceSprites.get(pieceId);
        if (sprite) {
            sprite.removeAllListeners();
            sprite.eventMode = 'static'; // 必须是 static 才能作为命中测试目标并冒泡到组容器
            sprite.cursor = 'default';
        }
    }

    // ==================== 底图相关方法 ====================

    /**
     * 创建底图（显示所有碎片正确位置的轮廓线）
     * @param slicedPieces 所有切割后的碎片数据
     * @param strokeColor 轮廓线颜色（默认灰色）
     * @param strokeWidth 轮廓线宽度（默认2）
     * @param strokeAlpha 轮廓线透明度（默认0.5）
     * @param showNumbers 是否显示编号（默认true）
     */
    createBaseMap(
        slicedPieces: SlicedPiece[],
        strokeColor?: number,
        strokeWidth?: number,
        strokeAlpha?: number,
        showNumbers: boolean = true
    ): void {
        // 解除缓存以进行重绘
        this.baseMapLayer.cacheAsBitmap = false;

        // 保存引用以便切换主题时重绘
        this.currentSlicedPieces = slicedPieces;

        // 清除旧的底图
        this.clearBaseMap();
        this.pieceMap.clear();

        // 使用当前主题配置
        const theme = this.currentTheme;
        const fillColor = theme.baseMapFillColor;
        const scale = theme.baseMapScale;

        for (let index = 0; index < slicedPieces.length; index++) {
            const slicedPiece = slicedPieces[index];
            const pieceId = `piece_${slicedPiece.coord.u}_${slicedPiece.coord.v}`;
            
            // 缓存数据供高亮使用
            this.pieceMap.set(pieceId, slicedPiece);

            // 创建容器
            const container = new PIXI.Container();

            // 创建 Graphics 对象
            const graphics = new PIXI.Graphics();

            // 1. 纯色填充
            graphics.beginFill(fillColor);

            // 2. 恢复内阴影描边 (模拟凹槽内部阴影，制造凹陷感)
            // 配合外层的 BevelFilter (凸起)，形成完美的凹坑效果
            graphics.lineStyle({
                width: 1,
                color: 0x000000,
                alpha: 0.3, // 通用阴影浓度
                alignment: 0 // Inner
            });
            
            // 解析 SVG 路径并绘制
            this.drawPathToGraphics(graphics, slicedPiece.pathData);
            graphics.endFill();

            // 2. 缩放产生间隙
            graphics.pivot.set(slicedPiece.center.x, slicedPiece.center.y);
            graphics.position.set(slicedPiece.center.x, slicedPiece.center.y);
            graphics.scale.set(scale);

            container.addChild(graphics);

            // 添加编号文本
            if (showNumbers && this.debugShowNumbers) {
                const labelText = `${index}`;
                const text = new PIXI.Text(labelText, {
                    fontFamily: 'Segoe UI, Helvetica, Arial',
                    fontSize: 11,
                    fontWeight: 'bold',
                    fill: theme.textColor,
                    align: 'center'
                });
                text.anchor.set(0.5);
                text.x = slicedPiece.center.x;
                text.y = slicedPiece.center.y;
                text.alpha = 0.6;
                container.addChild(text);
            }

            // 添加到底图层
            this.baseMapLayer.addChild(container);
            this.baseMapSprites.set(pieceId, container);
        }

        // 应用 Bevel Filter 制造 3D 立体感
        if (PIXI.filters && PIXI.filters.BevelFilter) {
            // 制造凹陷感的关键：反转光照颜色！
            // 左上角由亮变暗 (光照不到内壁) -> 黑色
            // 右下角由暗变亮 (光照到内壁) -> 白色
            const bevelFilter = new PIXI.filters.BevelFilter({
                rotation: 45,
                thickness: 1, // 增加厚度
                lightColor: 0x000000, // Top-left: Shadow (Shadow of the rim)
                lightAlpha: 0.6,
                shadowColor: 0xffffff, // Bottom-right: Highlight (Light catching the edge)
                shadowAlpha: 0.4,      // 稍微降低高光亮度，避免太刺眼
            });
            // 增加 padding 防止边缘裁剪
            bevelFilter.padding = 10;
            this.baseMapLayer.filters = [bevelFilter];
        }

        // 开启缓存，极大提升性能
        // 注意：在某些移动端设备上，过大的缓存可能导致内存问题，但对于 1024x1024 还可以接受
        this.baseMapLayer.cacheAsBitmap = true;
    }

    /**
     * 解析 SVG 路径数据并绘制到 Graphics 对象
     * 支持 M, L, Q, C, Z 命令
     */
    private drawPathToGraphics(graphics: any, pathData: string): void {
        // ... (this method remains as a fallback or for static drawing) ...
        // Actually I can just keep it as is, but I'll add the new methods after it.
        const commands = pathData.match(/[MLQCZ][^MLQCZ]*/gi);
        if (!commands) return;

        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;

        for (const cmd of commands) {
            const type = cmd[0].toUpperCase();
            const args = cmd.slice(1).trim().split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));

            switch (type) {
                case 'M': // MoveTo
                    if (args.length >= 2) {
                        currentX = args[0];
                        currentY = args[1];
                        startX = currentX;
                        startY = currentY;
                        graphics.moveTo(currentX, currentY);
                    }
                    break;

                case 'L': // LineTo
                    for (let i = 0; i < args.length; i += 2) {
                        if (i + 1 < args.length) {
                            currentX = args[i];
                            currentY = args[i + 1];
                            graphics.lineTo(currentX, currentY);
                        }
                    }
                    break;

                case 'Q': // Quadratic Bezier
                    for (let i = 0; i < args.length; i += 4) {
                        if (i + 3 < args.length) {
                            const cx = args[i];
                            const cy = args[i + 1];
                            currentX = args[i + 2];
                            currentY = args[i + 3];
                            graphics.quadraticCurveTo(cx, cy, currentX, currentY);
                        }
                    }
                    break;

                case 'C': // Cubic Bezier
                    for (let i = 0; i < args.length; i += 6) {
                        if (i + 5 < args.length) {
                            const cx1 = args[i];
                            const cy1 = args[i + 1];
                            const cx2 = args[i + 2];
                            const cy2 = args[i + 3];
                            currentX = args[i + 4];
                            currentY = args[i + 5];
                            graphics.bezierCurveTo(cx1, cy1, cx2, cy2, currentX, currentY);
                        }
                    }
                    break;

                case 'Z': // ClosePath
                    graphics.lineTo(startX, startY);
                    currentX = startX;
                    currentY = startY;
                    break;
            }
        }
    }

    /**
     * 计算三次贝塞尔曲线的近似长度
     */
    private getCurveLength(p0x: number, p0y: number, p1x: number, p1y: number, p2x: number, p2y: number, p3x: number, p3y: number): number {
        const samples = 10;
        let length = 0;
        let px = p0x, py = p0y;

        for (let i = 1; i <= samples; i++) {
            const t = i / samples;
            const mt = 1 - t;
            const mt2 = mt * mt;
            const mt3 = mt * mt2;
            const t2 = t * t;
            const t3 = t * t2;

            const x = mt3 * p0x + 3 * mt2 * t * p1x + 3 * mt * t2 * p2x + t3 * p3x;
            const y = mt3 * p0y + 3 * mt2 * t * p1y + 3 * mt * t2 * p2y + t3 * p3y;

            const dx = x - px;
            const dy = y - py;
            length += Math.sqrt(dx * dx + dy * dy);

            px = x;
            py = y;
        }
        return length;
    }

    /**
     * 解析路径为线段（用于动画绘制）
     */
    private parsePathToSegments(pathData: string): { segments: PathSegment[], totalLength: number } {
        const segments: PathSegment[] = [];
        let totalLength = 0;
        const commands = pathData.match(/[MLCZ][^MLCZ]*/gi); // Ignore Q for now as EdgeGenerator uses C
        if (!commands) return { segments, totalLength };

        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;

        for (const cmd of commands) {
            const type = cmd[0].toUpperCase() as 'M' | 'L' | 'C' | 'Z';
            const args = cmd.slice(1).trim().split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));

            if (type === 'M') {
                if (args.length >= 2) {
                    currentX = args[0];
                    currentY = args[1];
                    startX = currentX;
                    startY = currentY;
                    segments.push({ type: 'M', points: [currentX, currentY], startPoint: { x: 0, y: 0 }, length: 0 });
                }
            } else if (type === 'L') {
                for (let i = 0; i < args.length; i += 2) {
                    const x = args[i];
                    const y = args[i + 1];
                    const dx = x - currentX;
                    const dy = y - currentY;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    segments.push({ 
                        type: 'L', 
                        points: [x, y], 
                        startPoint: { x: currentX, y: currentY }, 
                        length: len 
                    });
                    totalLength += len;
                    currentX = x;
                    currentY = y;
                }
            } else if (type === 'C') {
                for (let i = 0; i < args.length; i += 6) {
                    const c1x = args[i], c1y = args[i+1];
                    const c2x = args[i+2], c2y = args[i+3];
                    const x = args[i+4], y = args[i+5];
                    const len = this.getCurveLength(currentX, currentY, c1x, c1y, c2x, c2y, x, y);
                    segments.push({ 
                        type: 'C', 
                        points: [c1x, c1y, c2x, c2y, x, y], 
                        startPoint: { x: currentX, y: currentY }, 
                        length: len 
                    });
                    totalLength += len;
                    currentX = x;
                    currentY = y;
                }
            } else if (type === 'Z') {
                const dx = startX - currentX;
                const dy = startY - currentY;
                const len = Math.sqrt(dx * dx + dy * dy);
                if (len > 0.1) { // Avoid zero-length close
                    segments.push({ 
                        type: 'L', // Treat Z as LineTo start
                        points: [startX, startY], 
                        startPoint: { x: currentX, y: currentY }, 
                        length: len 
                    });
                    totalLength += len;
                }
                currentX = startX;
                currentY = startY;
            }
        }

        return { segments, totalLength };
    }

    /**
     * 绘制部分路径
     */
    private drawPartialPath(graphics: any, segments: PathSegment[], targetLength: number): void {
        let drawnLength = 0;
        let started = false;

        for (const seg of segments) {
            if (seg.type === 'M') {
                graphics.moveTo(seg.points[0], seg.points[1]);
                started = true;
                continue;
            }

            if (!started) return;

            if (drawnLength + seg.length <= targetLength) {
                // Draw full segment
                if (seg.type === 'L') {
                    graphics.lineTo(seg.points[0], seg.points[1]);
                } else if (seg.type === 'C') {
                    graphics.bezierCurveTo(
                        seg.points[0], seg.points[1],
                        seg.points[2], seg.points[3],
                        seg.points[4], seg.points[5]
                    );
                }
                drawnLength += seg.length;
            } else {
                // Draw partial segment
                const remaining = targetLength - drawnLength;
                const t = remaining / seg.length;

                if (seg.type === 'L') {
                    const x = seg.startPoint.x + (seg.points[0] - seg.startPoint.x) * t;
                    const y = seg.startPoint.y + (seg.points[1] - seg.startPoint.y) * t;
                    graphics.lineTo(x, y);
                } else if (seg.type === 'C') {
                    // Split cubic bezier at t
                    // de Casteljau algorithm
                    const p0x = seg.startPoint.x, p0y = seg.startPoint.y;
                    const p1x = seg.points[0], p1y = seg.points[1];
                    const p2x = seg.points[2], p2y = seg.points[3];
                    const p3x = seg.points[4], p3y = seg.points[5];

                    const mt = 1 - t;
                    
                    const q0x = mt * p0x + t * p1x;
                    const q0y = mt * p0y + t * p1y;
                    const q1x = mt * p1x + t * p2x;
                    const q1y = mt * p1y + t * p2y;
                    const q2x = mt * p2x + t * p3x;
                    const q2y = mt * p2y + t * p3y;

                    const r0x = mt * q0x + t * q1x;
                    const r0y = mt * q0y + t * q1y;
                    const r1x = mt * q1x + t * q2x;
                    const r1y = mt * q1y + t * q2y;

                    const s0x = mt * r0x + t * r1x;
                    const s0y = mt * r0y + t * r1y;

                    graphics.bezierCurveTo(q0x, q0y, r0x, r0y, s0x, s0y);
                }
                break; // Stop after drawing partial segment
            }
        }
    }

    /**
     * 隐藏指定碎片的底图轮廓（当碎片归位时调用）
     */
    hideBaseMapPiece(pieceId: string): void {
        const graphics = this.baseMapSprites.get(pieceId);
        if (graphics) {
            graphics.visible = false;
        }

        // 同时也更新对应碎片的阴影状态为 placed (移除投影)
        const pieceSprite = this.pieceSprites.get(pieceId);
        if (pieceSprite) {
            this.applyDropShadow(pieceSprite, 'placed');
        }
    }

    /**
     * 显示指定碎片的底图轮廓
     */
    showBaseMapPiece(pieceId: string): void {
        const graphics = this.baseMapSprites.get(pieceId);
        if (graphics) {
            graphics.visible = true;
        }
    }

    /**
     * 清除底图
     */
    clearBaseMap(): void {
        for (const [, graphics] of this.baseMapSprites) {
            this.baseMapLayer.removeChild(graphics);
            graphics.destroy();
        }
        this.baseMapSprites.clear();
    }

    /**
     * 设置底图可见性
     */
    setBaseMapVisible(visible: boolean): void {
        this.baseMapLayer.visible = visible;
    }

    /**
     * 设置引导图（Ghost Image）
     * @param imageSource 图片源（URL 或 base64）
     * @param visible 是否显示
     * @param opacity 透明度
     */
    async setGuideImage(imageSource: string | null, visible: boolean, opacity: number = 0.5): Promise<void> {
        if (!this.guideLayer) return;

        this.guideLayer.visible = visible;
        this.guideLayer.alpha = opacity;

        if (visible && imageSource) {
            // 如果已经有内容且源相同，不再重新加载（简单判断）
            // 这里简化为每次清空重绘，或者检查是否已有 Sprite
            if (this.guideLayer.children.length === 0) {
                try {
                    const texture = await PIXI.Texture.fromURL(imageSource);
                    const sprite = new PIXI.Sprite(texture);
                    
                    // 与 ImageSlicer.resizeImage 一致：center-crop 填满 puzzleSize x puzzleSize
                    const coverScale = Math.max(this.puzzleSize / texture.width, this.puzzleSize / texture.height);
                    sprite.width = texture.width * coverScale;
                    sprite.height = texture.height * coverScale;
                    sprite.x = (this.puzzleSize - sprite.width) / 2;
                    sprite.y = (this.puzzleSize - sprite.height) / 2;

                    this.guideLayer.removeChildren();
                    this.guideLayer.addChild(sprite);

                    // 用所有碎片路径合成 mask，裁剪为拼图轮廓
                    if (this.currentSlicedPieces.length > 0) {
                        const maskGraphics = new PIXI.Graphics();
                        for (const piece of this.currentSlicedPieces) {
                            maskGraphics.beginFill(0xFFFFFF);
                            this.drawPathToGraphics(maskGraphics, piece.pathData);
                            maskGraphics.endFill();
                        }
                        this.guideLayer.addChild(maskGraphics);
                        sprite.mask = maskGraphics;
                    }
                } catch (e) {
                    console.error('Failed to load guide image:', e);
                }
            }
        }
    }

    /**
     * 获取效果层（供粒子效果使用）
     */
    getEffectsLayer(): any {
        return this.effectsLayer;
    }

    /**
     * 获取 PIXI 应用实例
     */
    getApp(): any {
        return this.app;
    }

    /**
     * 调整大小
     */
    resize(width: number, height: number): void {
        this.config.width = width;
        this.config.height = height;

        if (this.app) {
            this.app.renderer.resize(width, height);
            // 更新 stage 的 hitArea
            this.app.stage.hitArea = new PIXI.Rectangle(0, 0, width, height);
            // 重新计算缩放因子
            this.updateScale();
            // 重新绘制背景区域
            this.drawWorkAreaBackground();
        }
    }

    /**
     * 获取当前的边距配置（用于布局避让）
     */
    getPadding(): { top: number; bottom: number } {
        const height = this.config.height;
        const p = this.config.padding;
        let pt = 0, pb = 0;
        
        if (typeof p === 'number') {
            pt = pb = height * p;
        } else if (p) {
            pt = (p.top !== undefined && p.top <= 1) ? p.top * height : (p.top || 0);
            pb = (p.bottom !== undefined && p.bottom <= 1) ? p.bottom * height : (p.bottom || 0);
        }
        return { top: pt, bottom: pb };
    }

    /**
     * 销毁渲染器
     */
    destroy(): void {
        this.clearPieces();
        this.clearBaseMap();
        this.clearAtlas();

        // 清理长按计时器
        this.cancelLongPressTimer();

        // 移除全局事件监听
        if (this.app && this.app.view) {
            this.app.view.removeEventListener('wheel', this.handleWheel);
            this.app.view.removeEventListener('contextmenu', (e: Event) => e.preventDefault());
        }
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);

        if (this.app) {
            this.app.destroy(true, { children: true, texture: true, baseTexture: true });
        }
    }

    // ==================== 图集相关方法 ====================

    /**
     * 设置图集数据（从 TextureAtlasPacker 初始化）
     * @param atlasPacker 图集打包器实例
     */
    setAtlas(atlasPacker: TextureAtlasPacker): void {
        this.clearAtlas();

        // 从图集画布创建 PIXI 纹理
        const atlasCanvases = atlasPacker.getAtlases();
        for (const canvas of atlasCanvases) {
            const texture = PIXI.Texture.from(canvas);
            this.atlasTextures.push(texture);
        }

        // 复制帧数据
        this.atlasFrames = new Map(atlasPacker.getAllFrames());
        this.useAtlas = true;
    }

    /**
     * 使用图集添加碎片精灵（高性能模式）
     * @param piece 拼图碎片
     * @param slicedPiece 切割后的碎片数据（用于获取中心点等信息）
     * @param pieceKey 碎片在图集中的键（格式：u_v）
     */
    addPieceSpriteFromAtlas(
        piece: PuzzlePiece,
        slicedPiece: SlicedPiece,
        pieceKey?: string,
        index?: number
    ): void {
        const key = pieceKey || `${slicedPiece.coord.u}_${slicedPiece.coord.v}`;
        const frame = this.atlasFrames.get(key);
        console.log('addPieceSpriteFromAtlas:', { key, hasFrame: !!frame, useAtlas: this.useAtlas, frameCount: this.atlasFrames.size });

        if (!frame || !this.useAtlas) {
            // 回退到普通模式
            console.log('Falling back to addPieceSprite');
            this.addPieceSprite(piece, slicedPiece);
            return;
        }

        // 获取对应的图集纹理
        const atlasTexture = this.atlasTextures[frame.atlasIndex];
        if (!atlasTexture) {
            this.addPieceSprite(piece, slicedPiece);
            return;
        }

        // 创建碎片容器
        const container = new PIXI.Container();
        container.name = piece.id;

        // 从图集创建子纹理
        const frameRect = new PIXI.Rectangle(
            frame.x,
            frame.y,
            frame.width,
            frame.height
        );
        const pieceTexture = new PIXI.Texture(atlasTexture.baseTexture, frameRect);
        const sprite = new PIXI.Sprite(pieceTexture);

        // 设置锚点为中心
        sprite.anchor.set(0.5);

        // 计算相对于碎片中心的偏移
        const offsetX = slicedPiece.bounds.x + slicedPiece.bounds.width / 2 - slicedPiece.center.x;
        const offsetY = slicedPiece.bounds.y + slicedPiece.bounds.height / 2 - slicedPiece.center.y;
        sprite.x = offsetX;
        sprite.y = offsetY;

        container.addChild(sprite);

        // 设置初始位置
        container.x = piece.position.x;
        container.y = piece.position.y;
        container.rotation = piece.rotation * Math.PI / 180;

        // 设置交互
        container.eventMode = 'static';
        container.cursor = 'grab';
        // 设置 hitArea 为精灵的边界
        container.hitArea = new PIXI.Rectangle(
            -slicedPiece.bounds.width / 2,
            -slicedPiece.bounds.height / 2,
            slicedPiece.bounds.width,
            slicedPiece.bounds.height
        );

        this.setupPieceInteraction(container, piece.id);

        // 添加编号（仅调试模式）
        if (index !== undefined && this.debugShowNumbers) {
            this.addPieceNumber(container, index, slicedPiece.coord);
        }

        // 添加投影效果
        this.applyDropShadow(container);

        // 添加到层级
        this.piecesLayer.addChild(container);
        this.pieceSprites.set(piece.id, container);
    }

    /**
     * 批量使用图集添加碎片
     * @param pieces 拼图碎片数组
     * @param slicedPieces 切割后的碎片数据数组
     */
    addPiecesFromAtlas(
        pieces: PuzzlePiece[],
        slicedPieces: SlicedPiece[]
    ): void {
        // 创建坐标到切割碎片的映射
        const slicedMap = new Map<string, SlicedPiece>();
        for (const sp of slicedPieces) {
            const key = `${sp.coord.u}_${sp.coord.v}`;
            slicedMap.set(key, sp);
        }

        // 批量添加
        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            const key = `${piece.coord.u}_${piece.coord.v}`;
            const slicedPiece = slicedMap.get(key);
            if (slicedPiece) {
                this.addPieceSpriteFromAtlas(piece, slicedPiece, key, i);
            }
        }
    }

    /**
     * 检查是否正在使用图集模式
     */
    isUsingAtlas(): boolean {
        return this.useAtlas;
    }

    /**
     * 获取图集数量
     */
    getAtlasCount(): number {
        return this.atlasTextures.length;
    }

    /**
     * 获取渲染器尺寸
     */
    getSize(): { width: number; height: number } {
        return {
            width: this.config.width,
            height: this.config.height
        };
    }

    /**
     * 检查碎片精灵是否存在
     */
    hasPieceSprite(pieceId: string): boolean {
        return this.pieceSprites.has(pieceId);
    }

    /**
     * 获取所有碎片精灵（用于视觉退让效果）
     */
    getAllPieceSprites(): Map<string, any> {
        return this.pieceSprites;
    }

    /**
     * 将碎片动画飞入口袋
     * @param pieceId 碎片ID
     * @param targetScreenPos 口袋按钮的屏幕坐标
     * @param onComplete 动画完成回调
     */
    animateToPocket(pieceId: string, targetScreenPos: Vector2, onComplete?: () => void): void {
        const container = this.pieceSprites.get(pieceId);
        if (!container) {
            onComplete?.();
            return;
        }

        // 转换为逻辑坐标（因为 container 在 gameContainer 内）
        const targetLogicPos = this.screenToLogic(targetScreenPos);

        const startPos = { x: container.x, y: container.y };
        const startScale = { x: container.scale.x, y: container.scale.y };
        const startTime = performance.now();
        const duration = 400;

        const animate = (time: number) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = this.easeOutCubic(progress);

            container.x = startPos.x + (targetLogicPos.x - startPos.x) * eased;
            container.y = startPos.y + (targetLogicPos.y - startPos.y) * eased;
            container.scale.set(
                startScale.x * (1 - eased),
                startScale.y * (1 - eased)
            );
            container.alpha = 1 - eased;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                container.visible = false;
                // 恢复缩放和透明度，方便以后取出
                container.scale.set(startScale.x, startScale.y);
                container.alpha = 1;
                onComplete?.();
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * 获取碎片的纹理 Base64，用于 UI 展示
     */
    getPieceThumbnail(pieceId: string): string | null {
        const sliced = this.pieceMap.get(pieceId);
        if (!sliced) return null;
        return sliced.texture.toDataURL();
    }

    /**
     * 清空图集数据
     */
    clearAtlas(): void {
        // 销毁图集纹理
        for (const texture of this.atlasTextures) {
            texture.destroy(true);
        }
        this.atlasTextures = [];
        this.atlasFrames.clear();
        this.useAtlas = false;
    }
}
